import { useCallback, useEffect, useState } from 'react';

import { useCoreApi } from 'core/Api';
import { SUBDOMAINS, specificSubDomain } from 'core/Utils';

import Native from '../../utils/native';
import { useSelectHasEitherCardTypes } from '../queries/Core/payments';
import usePollingUpdater from '../queries/usePollingUpdater';
import usePlatformService from '../usePlatformService';
import { usePlatformExtension } from './usePlatformExtension';

interface IMessageFromNative {
  exitStatus: 200 | 500;
  sender: 'native' | 'native_browser';
  location?: string;
  action?: string;
  appState?: 'active' | 'background' | 'inactive' | 'unknown' | 'extension';
  extensionInstallationStatus?: boolean;
}

// You should be passing in referentially stable functions here and handling the side effects else where.
interface IOpenNativeBrowser {
  onSuccess?: () => void;
  onFailure?: () => void;
  onExit?: () => void;
}

const UseCreateShortSession = () => {
  const CoreApi = useCoreApi();

  const createShortSession = async (setLoader?: () => void) => {
    try {
      const { data } = await CoreApi.post('/short/sessions');
      return data;
    } catch (error) {
      setLoader?.();
      return false;
    }
  };
  return createShortSession;
};

const UseNativeIntercom = ({
  onSuccess,
  onFailure,
  onExit,
}: {
  onSuccess: () => void;
  onFailure: () => void;
  onExit: () => void;
}) => {
  const intercom = async (event: { data: string }) => {
    try {
      const json = JSON.parse(event.data);
      const { exitStatus } = json.params as IMessageFromNative;
      // if (sender == "native_browser") {
      // Temp:  If message contains an exitStatus, use in place of sender.
      if (exitStatus) {
        onExit?.();

        if (exitStatus == 200) {
          onSuccess?.();
        } else {
          onFailure?.();
        }
        window.removeEventListener('message', intercom);
      } else {
        console.debug('Handling out-of scope message');
      }
    } catch (e) {
      console.log(e);
    }
  };

  return intercom;
};

// This is the start of the hooks we'll build out to handle native logic so that we have this logic organized
export const useNativeAddCard = ({ onSuccess, onFailure }: IOpenNativeBrowser) => {
  const { data: hasPartnerCard, refetch: refetchCardTypes } = useSelectHasEitherCardTypes();
  const { pollQuery } = usePollingUpdater(useSelectHasEitherCardTypes);
  const createShortSession = UseCreateShortSession();
  const intercomParams = {
    onSuccess: () => {
      onSuccess?.();
      !hasPartnerCard && pollQuery(); // Refetch payment updates and then we should try to do some polling if we need to.
    },
    onFailure: () => {
      onFailure?.();
    },

    onExit: async () => await refetchCardTypes(), // On generic close, refetch card types.
  };

  const intercom = UseNativeIntercom(intercomParams);

  const createNativeEventListener = useCallback(async () => {
    window.addEventListener('message', intercom);
  }, [pollQuery]);

  const openNativeAddCard = async (disable?: () => void) => {
    await createNativeEventListener();
    const shortSession = await createShortSession(disable);
    if (shortSession) {
      const path = `/wallet/add-card?token=${shortSession?.token}`;
      Native.postURL(`${specificSubDomain(SUBDOMAINS.WALLET)}${path}`);
      disable && setTimeout(() => disable(), 500);
    }
  };

  useEffect(() => {
    return () => {
      // Always attempt to clean up the listener on dismount, not referentially stable
      window.removeEventListener('message', intercom);
    };
  }, []);

  return { openNativeAddCard };
};

interface OpenExtension {
  onSuccess?: (cb?: any) => void;
  onFailure?: (cb?: any) => void;
  onExit?: (cb?: any) => void;
}

export const useNativeInstallExtension = ({ onSuccess, onExit, onFailure }: OpenExtension) => {
  const Platform = usePlatformService();
  const [isValidating, setIsValidating] = useState(false);
  const [hasReturned, setHasReturned] = useState(false);
  const [couldNotValidate, setCouldNotValidate] = useState(false);

  const { installPlatformExtension } = usePlatformExtension();

  const installNativeExtension = useCallback(() => {
    if (!Platform.isNative) return false;
    if (isValidating) {
      // If we are already validating, we can re-initiate validation using the previously mounted el
      setTimeout(() => {
        installPlatformExtension();
      }, 500);
    }
    setIsValidating(true);
  }, [Platform.isNative, isValidating]);

  useEffect(() => {
    const handleMessage = (event: any) => {
      try {
        const message = JSON.parse(event.data);
        const { appState, extensionInstallationStatus } = message.params as IMessageFromNative;
        if (appState == 'active') {
          if (extensionInstallationStatus) {
            onSuccess?.();
          } else {
            onFailure?.();
          }
          setHasReturned(true);
        }
      } catch (error) {
        setCouldNotValidate(true);
        onFailure?.();
      }
    };

    if (isValidating) {
      window.addEventListener('message', handleMessage);
      setTimeout(() => {
        installPlatformExtension();
      }, 500);
    } else {
      window.removeEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isValidating]);

  useEffect(() => {
    if (isValidating && hasReturned) {
      setTimeout(() => {
        setCouldNotValidate(true);
      }, 500);
    }
  }, [hasReturned]);

  return {
    installNativeExtension,
    isValidating,
    couldNotValidate,
  };
};
